import { Extension } from '@tiptap/core';

// Per-block line spacing and space before/after, the way Docs/Word do it:
// the values live on the node (so they round-trip through the saved HTML and
// apply to just the selected paragraphs) instead of on a document-wide CSS
// variable. Blocks with nothing set fall back to the stylesheet defaults.

const SPACING_TYPES = ['paragraph', 'heading', 'bulletList', 'orderedList', 'taskList', 'blockquote'];

// One "line" of space, matching the Add space before paragraph menu item
export const SPACE_STEP_PT = 12;

/** Marks a block the writer wants to start a page. Read by the paginator. */
export const PAGE_BREAK_CLASS = 'page-break-before';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        paragraphSpacing: {
            setLineSpacing: (lineHeight: number | null) => ReturnType;
            setBlockSpacing: (spacing: { before?: number | null; after?: number | null }) => ReturnType;
            addSpaceBefore: () => ReturnType;
            removeSpaceBefore: () => ReturnType;
            togglePageBreakBefore: () => ReturnType;
        };
    }
}

const parsePt = (value: string): number | null => {
    if (!value) return null;
    const parsed = parseFloat(value);
    if (Number.isNaN(parsed)) return null;
    // Browsers report computed px on inline styles they normalize; the value
    // we write is always pt, so only trust an explicit pt suffix.
    return value.includes('pt') ? parsed : Math.round((parsed * 72) / 96);
};

export const ParagraphSpacing = Extension.create({
    name: 'paragraphSpacing',

    addGlobalAttributes() {
        return [
            {
                types: SPACING_TYPES,
                attributes: {
                    lineHeight: {
                        default: null,
                        parseHTML: (element) => {
                            const value = parseFloat(element.style.lineHeight);
                            return Number.isNaN(value) ? null : value;
                        },
                        renderHTML: (attributes) =>
                            attributes.lineHeight ? { style: `line-height: ${attributes.lineHeight}` } : {},
                    },
                    spaceBefore: {
                        default: null,
                        parseHTML: (element) => parsePt(element.style.marginTop),
                        renderHTML: (attributes) =>
                            attributes.spaceBefore === null || attributes.spaceBefore === undefined
                                ? {}
                                : { style: `margin-top: ${attributes.spaceBefore}pt` },
                    },
                    spaceAfter: {
                        default: null,
                        parseHTML: (element) => parsePt(element.style.marginBottom),
                        renderHTML: (attributes) =>
                            attributes.spaceAfter === null || attributes.spaceAfter === undefined
                                ? {}
                                : { style: `margin-bottom: ${attributes.spaceAfter}pt` },
                    },
                    // A break the writer asked for, as opposed to one the text
                    // ran into. Carried as a class so it survives the saved
                    // HTML and is visible to the paginator, which is what
                    // actually moves the block to the next page.
                    pageBreakBefore: {
                        default: false,
                        parseHTML: (element) => element.classList.contains(PAGE_BREAK_CLASS),
                        renderHTML: (attributes) =>
                            attributes.pageBreakBefore ? { class: PAGE_BREAK_CLASS } : {},
                    },
                },
            },
        ];
    },

    addCommands() {
        // Applies `patch` to every spacing-capable block the selection touches
        const updateBlocks =
            (patch: (attrs: Record<string, any>) => Record<string, any> | null) =>
            ({ tr, state, dispatch }: any) => {
                const { from, to } = state.selection;
                let changed = false;
                state.doc.nodesBetween(from, to, (node: any, pos: number) => {
                    if (!SPACING_TYPES.includes(node.type.name)) return;
                    const next = patch(node.attrs);
                    if (!next) return;
                    // Re-marking a block with the attributes it already has is
                    // not free: it dirties the autosave, adds an undo step, and
                    // re-creates the node, which drops any widget decoration
                    // sitting at its first position — page breaks included.
                    if (Object.keys(next).every((key) => node.attrs[key] === next[key])) return;
                    tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...next });
                    changed = true;
                });
                if (changed && dispatch) dispatch(tr);
                return changed;
            };

        return {
            setLineSpacing: (lineHeight) => updateBlocks(() => ({ lineHeight })),
            setBlockSpacing: (spacing) =>
                updateBlocks(() => ({
                    ...(spacing.before !== undefined ? { spaceBefore: spacing.before } : {}),
                    ...(spacing.after !== undefined ? { spaceAfter: spacing.after } : {}),
                })),
            addSpaceBefore: () =>
                updateBlocks((attrs) => ({ spaceBefore: (attrs.spaceBefore ?? 0) + SPACE_STEP_PT })),
            // Nothing to remove from a block that never had space set — leave
            // it on the document default rather than stamping 0pt across every
            // paragraph in the selection.
            removeSpaceBefore: () => updateBlocks((attrs) => (attrs.spaceBefore ? { spaceBefore: 0 } : null)),
            togglePageBreakBefore: () =>
                updateBlocks((attrs) => ({ pageBreakBefore: !attrs.pageBreakBefore })),
        };
    },
});
